/** Copyright (c) 2021 T. Rowe Price. All rights reserved. */

import { createSelector } from 'reselect';
import { isEmpty, isNil, kebabCase, some } from 'lodash';
import {
  COLLEGE_SAVINGS,
  MUTUAL_FUNDS,
  ACTIVE_PLUS,
  ADVICE_MANAGED
} from 'state/statements/StatementsSelectors';
import {
  ACTIVE_ADVISORY_CLASS,
  ACTIVE_PLUS_CLASS,
  getSelectedProductClasses,
  isActivePlusProductClass,
  isAdviceProductClass
} from 'services/Products';
import { getRateOfReturnFilter } from './RateOfReturnFilterSelectors';
import { shouldUseAggregatedPerformance } from './PerformancePageSelectors';
import { ALL_LABEL } from 'services/Filters';

// product type for model benchmarks
export const MODEL_BENCHMARK = 'modelBenchmark';
export const PERSONAL_INVESTMENT = 'personalInvestment';
// css class name for ras or app when selected and showing benchmark
export const RAS_OR_APP_WITH_BENCHMARK = 'ras-or-app-with-benchmark';

export const isBenchmark = (type) => type === MODEL_BENCHMARK;
export const isPersonalInvestment = (type) => type === PERSONAL_INVESTMENT;
export const containsBenchmark = (selectedPerf) =>
  some(selectedPerf, (data) => isBenchmark(data.productType));
export const getClassForProductType = (selectedPerf, type, rateClass) => {
  if (selectedPerf.length === 1) {
    return `bar ${rateClass}`;
  }

  return containsBenchmark(selectedPerf) && !isBenchmark(type)
    ? RAS_OR_APP_WITH_BENCHMARK
    : kebabCase(type);
};

export const getBarComponentId = (productType, idx) =>
  kebabCase(productType) + '-' + idx;

export const getProductPerformanceData = (state) => state.productPerformance;
export const getAggregatedProductPerformanceData = (state) =>
  state.aggregatedProductPerformance;
export const getProductTypes = (state) => state.productTypes;

export const getRasPerformanceData = createSelector(
  getProductPerformanceData,
  (rasProductPerformance) => {
    return isNil(rasProductPerformance)
      ? []
      : rasProductPerformance.filter(
          (product) => product.productClass === ADVICE_MANAGED
        );
  }
);

export const getActivePlusPerformanceData = createSelector(
  getProductPerformanceData,
  (appProductPerformance) => {
    return isNil(appProductPerformance)
      ? []
      : appProductPerformance.filter(
          (product) => product.productClass === ACTIVE_PLUS
        );
  }
);

export const getMutualFundPerformanceData = createSelector(
  getProductPerformanceData,
  (mutualFundProductPerformance) => {
    return isNil(mutualFundProductPerformance)
      ? []
      : mutualFundProductPerformance.filter(
          (product) => product.productClass === MUTUAL_FUNDS
        );
  }
);

export const getCollegeSavingPlansData = createSelector(
  getProductPerformanceData,
  (collegeSavingsProductPerformance) => {
    return isNil(collegeSavingsProductPerformance)
      ? []
      : collegeSavingsProductPerformance.filter(
          (product) => product.productClass === COLLEGE_SAVINGS
        );
  }
);

export const getModelBenchmark = createSelector(
  getProductPerformanceData,
  (productPerformance) => {
    const advicePerformance = productPerformance.find(
      (product) =>
        isAdviceProductClass(product.productClass) ||
        isActivePlusProductClass(product.productClass)
    );

    return isNil(advicePerformance)
      ? []
      : isNil(advicePerformance.benchmarks)
      ? []
      : advicePerformance.benchmarks.map((benchmark) => {
          // benchmark has non-standard prop names :( must return new object with modified names for the graph
          // TODO: remove this mapping when/if these prop names are updated

          const {
            monthToDateRateOfReturn,
            quarterToDateRateOfReturn,
            lastQuarterRateOfReturn,
            ytdRateOfReturn,
            oneYearRateOfReturn,
            threeYearRateOfReturn,
            fiveYearRateOfReturn,
            tenYearRateOfReturn,
            rateOfReturnSinceInception
          } = benchmark;

          return {
            productType: MODEL_BENCHMARK,
            benchmark: benchmark.benchmark,
            monthToDateRateOfReturn,
            quarterToDateRateOfReturn,
            lastQuarterReturn: lastQuarterRateOfReturn,
            ytdReturn: ytdRateOfReturn,
            oneYearReturn: oneYearRateOfReturn,
            threeYearReturn: threeYearRateOfReturn,
            fiveYearReturn: fiveYearRateOfReturn,
            tenYearReturn: tenYearRateOfReturn,
            inceptionReturn: rateOfReturnSinceInception
          };
        });
  }
);

export const getActivePlusBenchmarks = createSelector(
  getActivePlusPerformanceData,
  (performance) => {
    if (!isEmpty(performance)) {
      return performance.reduce((updatedBenchmarks, currData) => {
        const { benchmarks } = currData;

        if (!isNil(currData.accountId)) {
          return {
            ...updatedBenchmarks,
            [currData.accountId]: isNil(benchmarks)
              ? []
              : currData.benchmarks.map((benchmark) => {
                  const {
                    lastQuarterRateOfReturn,
                    ytdRateOfReturn,
                    oneYearRateOfReturn,
                    threeYearRateOfReturn,
                    fiveYearRateOfReturn,
                    tenYearRateOfReturn,
                    rateOfReturnSinceInception
                  } = benchmark;

                  return {
                    ...benchmark,
                    lastQuarterReturn: lastQuarterRateOfReturn,
                    ytdReturn: ytdRateOfReturn,
                    oneYearReturn: oneYearRateOfReturn,
                    threeYearReturn: threeYearRateOfReturn,
                    fiveYearReturn: fiveYearRateOfReturn,
                    tenYearReturn: tenYearRateOfReturn,
                    inceptionReturn: rateOfReturnSinceInception
                  };
                })
          };
        }
        return { ...updatedBenchmarks };
      }, {});
    }

    return {};
  }
);

export const getGraphPerformanceData = createSelector(
  getProductPerformanceData,
  getAggregatedProductPerformanceData,
  getModelBenchmark,
  getRateOfReturnFilter,
  shouldUseAggregatedPerformance,
  (
    productPerformance,
    aggregatedProductPerformance,
    modelBenchmark,
    performanceFilter,
    shouldUseAggregatedPerformance
  ) => {
    const { filter } = performanceFilter;

    // In the case of only MF & 529s, show the aggregated
    // performance data instead of the individual product classes
    const performance = shouldUseAggregatedPerformance
      ? aggregatedProductPerformance
      : productPerformance;

    // Product call returns RAS data with class of "adviceManaged"; this standardizes it with the rest of the app
    const updatedProductPerformance = performance.map((prod) => {
      const updatedProduct = isAdviceProductClass(prod.productClass)
        ? ACTIVE_ADVISORY_CLASS
        : prod.productClass;
      return {
        ...prod,
        productType: updatedProduct
      };
    });

    const rasOnly =
      updatedProductPerformance.length === 1 &&
      updatedProductPerformance[0].productType === ACTIVE_ADVISORY_CLASS;
    const rasSelected =
      rasOnly ||
      (filter.label !== ALL_LABEL &&
        getSelectedProductClasses(filter.label).includes(
          ACTIVE_ADVISORY_CLASS
        ));

    const appOnly =
      updatedProductPerformance.length === 1 &&
      isActivePlusProductClass(updatedProductPerformance[0].productType);
    const appSelected =
      appOnly ||
      (filter.label !== ALL_LABEL &&
        getSelectedProductClasses(filter.label).includes(ACTIVE_PLUS_CLASS));

    if (rasSelected || appSelected) {
      return [...modelBenchmark, ...updatedProductPerformance];
    }

    return updatedProductPerformance;
  }
);
